import { IHasOptionalChildren } from '@/lib/interface';
import { useEffect, useState } from 'react';
import { CircleX } from 'lucide-react';
import { TNullable } from '@/types';
import { cn } from '@/lib/utils';
import {
	DropdownMenuSeparator,
	DropdownMenuContent,
	DropdownMenuTrigger,
	DropdownMenuGroup,
	DropdownMenuItem,
	DropdownMenu,
	Button,
	Input,
} from '@/components/ui';

interface IState {
	value?: TNullable<string>;
	open: boolean;
}

type TProps = IHasOptionalChildren & {
	variant?:
		| 'default'
		| 'link'
		| 'secondary'
		| 'destructive'
		| 'outline'
		| 'ghost'
		| null
		| undefined;
	onOpenChange?: (open: boolean, state: IState) => IState;
	onChange: (value?: TNullable<string>) => void;
	enableBoutonClear?: boolean;
	value?: TNullable<string>;
	appendTextValue?: boolean;
	inputPlaceholder?: string;
	onReset?: () => void;
	ButtonText?: string;
	disabled?: boolean;
	className?: string;
	title?: string;
	text?: string;
	popup?: {
		side?: 'top' | 'right' | 'bottom' | 'left';
		align?: 'end' | 'center' | 'start';
	};
};

const VARIABLES = {
	initState: {
		open: false,
	} as IState,
};

const ButtonFilterInputText = ({
	inputPlaceholder,
	onOpenChange,
	className,
	disabled,
	children,
	onChange,
	onReset,
	variant,
	value,
	title,
	popup,
	text,
	ButtonText = 'Appliquer',
	enableBoutonClear = true,
	appendTextValue = true,
}: TProps) => {
	const [state, setState] = useState<IState>(VARIABLES.initState);

	useEffect(() => {
		setState(() => ({ ...VARIABLES.initState, value }));
	}, [value]);

	const hasValue = !!state.value;

	const handleOnClickButton = () => {
		setState(l => ({ ...l, open: !l.open }));
	};

	const handleOnClickBtnReset = () => {
		// Reset value
		setState(VARIABLES.initState);
		// Apply filter
		onChange();
		// Reset
		onReset?.();
	};

	const handleOnClickButtonApply = () => {
		setState(l => ({ ...l, open: false }));
		onChange(state.value);
	};

	const handleOnValueChange = (e: any) => {
		setState(l => ({ ...l, value: e.target.value }));
	};

	const handleOnOpenChange = (open: boolean) => {
		if (onOpenChange) {
			const newState = onOpenChange(open, state);
			setState(l => ({ ...l, ...newState }));
		}
	};

	return (
		<div className="flex items-center my-auto">
			<DropdownMenu modal={false} onOpenChange={handleOnOpenChange}>
				<DropdownMenuTrigger asChild>
					<Button
						className={cn(
							`space-x-1 border border-dashed text-xs items-center`,
							enableBoutonClear && hasValue && 'border-r-0 rounded-r-none',
							className,
						)}
						variant={variant ? variant : hasValue ? 'secondary' : 'ghost'}
						onClick={handleOnClickButton}
						disabled={disabled}
						title={title}
						size={'sm'}
					>
						{children && children}
						{!children && (
							<>
								<span>{text}</span>
								{appendTextValue && hasValue && (
									<span className="text-purple-600 font-semibold">: {state.value}</span>
								)}
							</>
						)}
					</Button>
				</DropdownMenuTrigger>
				<DropdownMenuContent
					align={popup?.align ?? 'start'}
					className="min-w-40"
					side={popup?.side}
				>
					<DropdownMenuGroup>
						<Input
							className="h-8 text-sm"
							value={state.value ?? ''}
							placeholder={inputPlaceholder}
							onChange={handleOnValueChange}
						/>
						<DropdownMenuSeparator />
						<DropdownMenuItem className="p-0">
							<Button
								className="w-full h-8 m-0 p-0 text-xs"
								onClick={handleOnClickButtonApply}
								variant={'default'}
								size={'sm'}
							>
								{ButtonText}
							</Button>
						</DropdownMenuItem>
					</DropdownMenuGroup>
				</DropdownMenuContent>
			</DropdownMenu>
			{enableBoutonClear && hasValue && (
				<Button
					className="space-x-1 border border-dashed border-l-0 rounded-l-none text-xs"
					variant={hasValue ? 'secondary' : 'ghost'}
					title="Supprimer le filtre"
					onClick={handleOnClickBtnReset}
					disabled={disabled}
					size={'sm'}
				>
					<CircleX size={16} />
				</Button>
			)}
		</div>
	);
};

export default ButtonFilterInputText;
